import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import {
  DEFAULT_STORYBOARD_IFRAME_STATE,
  useStoryboardIframeStore,
} from "@/store/zustand/storyboardIframeStore";
import { useCanvasId, usePrevious } from "@/utils/utils";
import { cn } from "@/lib/utils";
import { CANVAS_WIDTH, CANVAS_HEIGHT } from "./utils";
import { useStoryboardUrl } from "./hooks/useStoryboardUrl";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { v4 as uuidv4 } from "uuid";

interface IframeSlot {
  key: string;
  lastSrc: string;
  assignedStoryboardId: string | null; // Track which storyboard currently uses this slot
}

interface PersistentIframeProps {
  slot: IframeSlot;
  storyboard: any | null;
  storyboardsIdsToHide: Set<string>;
  minZIndex: number;
  onLoadIFrame: (
    iframeId: string,
    contentWindow: WindowProxy | null,
    storyboard: any,
    originalStoryboardUrl: string
  ) => void;
  iframeRef: (storyboard: any, ref: any) => void;
  onMouseOut: () => void;
  onMouseUp: (e: React.MouseEvent) => void;
}

const PersistentIframe: React.FC<PersistentIframeProps> = React.memo(
  ({
    slot,
    storyboard,
    storyboardsIdsToHide,
    minZIndex,
    onLoadIFrame,
    iframeRef,
    onMouseOut,
    onMouseUp,
  }) => {
    const canvasId = useCanvasId();
    const canvasIdToDevServerUrl = useSelector(
      (state: any) => state.iframe.canvasIdToDevServerUrl || {}
    );
    const devServerUrl = canvasIdToDevServerUrl[canvasId];

    // Always call hooks - use dummy storyboard if null to avoid conditional hook calls
    const dummyStoryboard = storyboard || { id: "", type: "", created: false };
    const baseStoryboardUrl = useStoryboardUrl(dummyStoryboard);
    const storyboardIdToStatus = useCanvasStore(
      (state) => state.storyboardIdToStatus || {}
    );

    // Get storyboard-specific iframe state - directly access to ensure reactivity
    const storyboardIframeState = useStoryboardIframeStore(
      (state) =>
        state.storyboardIframeStates[storyboard?.id] ||
        DEFAULT_STORYBOARD_IFRAME_STATE
    );

    const [thisSlotRefreshKey, setThisSlotRefreshKey] = useState(0);

    // No matter which storyboard changes, we keep an internal refresh key for the slot
    useEffect(() => {
      setThisSlotRefreshKey(thisSlotRefreshKey + 1);
    }, [storyboardIframeState.refreshKey]);

    // Infer if slot is active from whether it has an assigned storyboard
    const isActive = !!slot.assignedStoryboardId;

    // Use override URL if available, otherwise use base URL
    const actualStoryboardUrl = storyboard
      ? storyboardIframeState?.overrideUrl || baseStoryboardUrl
      : "";

    const status = storyboard
      ? storyboardIdToStatus[storyboard.id]?.status
      : null;
    const isHidden = storyboard
      ? storyboardsIdsToHide.has(storyboard.id)
      : false;

    const [hasLoaded, setHasLoaded] = useState(false);
    const prevAssignedStoryboardId = usePrevious(slot.assignedStoryboardId);

    const iframeRefInternal = useRef<HTMLIFrameElement | null>(null);
    useEffect(() => {
      return () => {
        // Cancel pending loads, clear refs, etc.
        if (iframeRefInternal.current) {
          iframeRefInternal.current = null;
        }
      };
    }, []);

    useEffect(() => {
      // The onLoad won't trigger again, so trigger manually
      if (slot.assignedStoryboardId && !prevAssignedStoryboardId && hasLoaded) {
        // We just got assigned a storyboard, trigger a load
        onLoadIFrame(
          `iframe-${slot.key}`,
          iframeRefInternal.current?.contentWindow || null,
          storyboard,
          actualStoryboardUrl
        );
      }
    }, [slot.assignedStoryboardId]);

    // Store the last src when we have an active storyboard
    const [lastSrc, setLastSrc] = useState(slot.lastSrc || "");

    useEffect(() => {
      if (isActive && actualStoryboardUrl && actualStoryboardUrl !== lastSrc) {
        setLastSrc(actualStoryboardUrl);
      }
    }, [isActive, actualStoryboardUrl, lastSrc]);

    // Use current storyboard URL if active, otherwise keep last src
    // Also include refresh key to force reload when needed
    const currentSrc =
      isActive && actualStoryboardUrl ? actualStoryboardUrl : lastSrc;
    const srcWithRefreshKey = storyboardIframeState?.refreshKey
      ? `${currentSrc}${currentSrc.includes("?") ? "&" : "?"}refreshKey=${
          storyboardIframeState.refreshKey
        }`
      : currentSrc;

    // Calculate opacity based on storyboard state
    let calculatedOpacity = 0;
    if (isActive) {
      if (
        storyboard?.renderStatus === "placeholder" ||
        storyboard?.renderStatus === "generating"
      ) {
        calculatedOpacity = 0;
      } else if (storyboardIframeState?.droppingStoryboard) {
        calculatedOpacity = 0.7;
      } else {
        calculatedOpacity = storyboardIframeState?.opacity ?? 1;
      }
    }

    // Determine pointer events
    const shouldHavePointerEvents =
      isActive &&
      (storyboardIframeState?.pointerEventsEnabled ?? true) &&
      !storyboardIframeState?.droppingStoryboard;

    // Get background - could include gradients or other styling
    const iframeBackground = storyboardIframeState?.background || "white";

    // Calculate z-index: should be storyboard.zIndex - minZIndex (1 less than storyboard itself)
    const iframeZIndex = storyboard?.zIndex ? storyboard.zIndex - minZIndex : 0;

    return (
      <iframe
        key={`${slot.key}-${thisSlotRefreshKey || 0}`} // Include refresh key in key to force remount
        id={`iframe-${slot.key}`}
        src={srcWithRefreshKey}
        className={cn("iframe1", storyboard?.id)}
        style={{
          position: "absolute" as const,
          // If no active storyboard, move iframe off-screen but keep it rendered
          left: isActive
            ? CANVAS_WIDTH / 2 + (storyboard?.x || 0)
            : -(CANVAS_WIDTH * 2), // Move off-screen
          top: isActive
            ? CANVAS_HEIGHT / 2 + (storyboard?.y || 0)
            : -(CANVAS_HEIGHT * 2), // Move off-screen
          width: storyboard?.width || 0,
          height: storyboard?.height || 0,
          opacity: calculatedOpacity,
          display: isActive && isHidden ? "none" : "block",
          pointerEvents: shouldHavePointerEvents ? "auto" : "none",
          borderRadius: "6px",
          overflow: "hidden",
          transition: "opacity 0.2s ease-in-out, left 0s, top 0s",
          background: iframeBackground,
          zIndex: iframeZIndex,
        }}
        onLoad={(e: React.SyntheticEvent<HTMLIFrameElement>) => {
          if (isActive && storyboard) {
            const iframe = e.currentTarget;

            iframeRefInternal.current = iframe;

            const baseStoryboardUrl =
              devServerUrl?.split("/")?.slice(0, 3)?.join("/") || "";

            onLoadIFrame(
              `iframe-${slot.key}`,
              iframe.contentWindow,
              storyboard,
              baseStoryboardUrl
            );
            setHasLoaded(true);
          }
        }}
        onMouseOut={isActive ? onMouseOut : undefined}
        onMouseUp={isActive ? onMouseUp : undefined}
        ref={(ref: HTMLIFrameElement) => {
          if (ref && isActive && storyboard) {
            iframeRef(storyboard, ref);
          }
        }}
      />
    );
  }
);

interface IframePoolProps {
  storyboards: any[];
  storyboardsIdsToHide: Set<string>;
  minZIndex: number;
  onLoadIFrame: (
    iframeId: string,
    contentWindow: WindowProxy | null,
    storyboard: any,
    originalStoryboardUrl: string
  ) => void;
  iframeRef: (storyboard: any, ref: any) => void;
  onMouseOut: () => void;
  onMouseUp: (e: React.MouseEvent) => void;
}

export const IframePool: React.FC<IframePoolProps> = ({
  storyboards,
  storyboardsIdsToHide,
  minZIndex,
  onLoadIFrame,
  iframeRef,
  onMouseOut,
  onMouseUp,
}) => {
  const storyboardIdToStatus = useCanvasStore(
    (state) => state.storyboardIdToStatus || {}
  );
  const setIframeAssignment = useCanvasStore(
    (state) => state.setIframeAssignment
  );

  // Maintain persistent iframe slots with stable assignments
  const [iframeSlots, setIframeSlots] = useState<IframeSlot[]>([]);

  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );

  const canvasId = useCanvasId();

  const canvasIdToDevServerUrl = useSelector(
    (state: any) => state.iframe.canvasIdToDevServerUrl
  );
  const devServerUrl = canvasIdToDevServerUrl[canvasId];

  // Filter storyboards that need iframes (correct status and have dimensions)
  const storyboardsWithIframes = useMemo(() => {
    if (!Array.isArray(storyboards)) {
      return [];
    }

    return storyboards.filter((storyboard) => {
      if (projectLaunchStatus !== "succeeded") {
        return false;
      }

      if (!storyboard || !storyboard.id) {
        return false;
      }

      const status = storyboardIdToStatus[storyboard.id]?.status;
      const shouldRenderIframe = status === "loading" || status === "live";
      return shouldRenderIframe && storyboard.width && storyboard.height;
    });
  }, [storyboards, storyboardIdToStatus]);

  // Update iframe slots with stable assignments during active use
  useEffect(() => {
    if (!Array.isArray(storyboardsWithIframes)) {
      return;
    }

    setIframeSlots((prevSlots) => {
      let newSlots = [...(prevSlots || [])];

      // Get IDs of storyboards that currently need iframes
      const activeStoryboardIds = new Set(
        storyboardsWithIframes.map((sb) => sb?.id).filter(Boolean)
      );

      // Step 1: Free up slots for storyboards that no longer need iframes
      newSlots.forEach((slot) => {
        if (slot && slot.assignedStoryboardId) {
          if (!activeStoryboardIds.has(slot.assignedStoryboardId)) {
            // This storyboard no longer needs an iframe - free the slot
            slot.assignedStoryboardId = null;
            // Keep lastSrc so iframe stays with its content until reused
          }
        }
      });

      // Step 2: Assign storyboards to slots (keeping existing assignments)
      storyboardsWithIframes.forEach((storyboard) => {
        if (!storyboard || !storyboard.id) return;

        // Check if this storyboard already has an assigned slot
        let assignedSlotIndex = newSlots.findIndex(
          (slot) => slot && slot.assignedStoryboardId === storyboard.id
        );

        // If no existing assignment, find first available slot
        if (assignedSlotIndex === -1) {
          assignedSlotIndex = newSlots.findIndex(
            (slot) => slot && slot.assignedStoryboardId === null
          );

          // If no existing slots available, create a new one
          if (assignedSlotIndex === -1) {
            assignedSlotIndex = newSlots.length;
            newSlots.push({
              key: `iframe-${uuidv4()}`,
              lastSrc: "",
              assignedStoryboardId: null,
            });
          }

          // Assign this slot to the storyboard (until it no longer needs iframe)
          newSlots[assignedSlotIndex].assignedStoryboardId = storyboard.id;
        }

        // Set canvas store assignment
        if (setIframeAssignment) {
          setIframeAssignment(
            storyboard.id,
            `iframe-${newSlots[assignedSlotIndex].key}`
          );
        }
      });

      // If all slots are assigned, create a new slot to pre-load it and set the src to the base dev server url
      if (newSlots.length === storyboardsWithIframes.length) {
        newSlots.push({
          key: `iframe-${uuidv4()}`,
          lastSrc: devServerUrl || "",
          assignedStoryboardId: null,
        });
      }

      // If there are more than 2 unused slots, remove the ones that are not assigned
      // Until there are only 2 unused slots
      const totalUnusedSlots = newSlots.filter(
        (slot) => !slot.assignedStoryboardId
      ).length;
      if (totalUnusedSlots > 2) {
        const unusedSlotsToRemove = totalUnusedSlots - 2;
        let removedCount = 0;

        // Remove unused slots from the end (keeping the structure intact)
        newSlots = newSlots.filter((slot) => {
          if (
            !slot.assignedStoryboardId &&
            removedCount < unusedSlotsToRemove
          ) {
            removedCount++;
            return false; // Remove this slot
          }
          return true; // Keep this slot
        });
      }

      return newSlots;
    });
  }, [storyboardsWithIframes, setIframeAssignment]);

  return (
    <>
      {Array.isArray(iframeSlots) &&
        iframeSlots.map((slot) => {
          if (!slot) return null;

          // Find the storyboard for this slot
          const storyboard =
            storyboards.find((sb) => sb.id === slot.assignedStoryboardId) ||
            null;

          return (
            <PersistentIframe
              key={slot.key}
              slot={slot}
              storyboard={storyboard}
              storyboardsIdsToHide={storyboardsIdsToHide}
              minZIndex={minZIndex}
              onLoadIFrame={onLoadIFrame}
              iframeRef={iframeRef}
              onMouseOut={onMouseOut}
              onMouseUp={onMouseUp}
            />
          );
        })}
    </>
  );
};
